import clsx from "clsx";
import { ReactNode, ThHTMLAttributes, useCallback, useState } from "react";
import { SortDir } from "./SortItem";
import { getNextTableSort } from "./sort";

export type UseTableSortOptions<SortBy extends string> = {
  initialBy: SortBy;
  initialDir: SortDir;
  defaultDir: (value: SortBy) => SortDir;
};

export const useTableSort = <SortBy extends string>({
  initialBy,
  initialDir,
  defaultDir,
}: UseTableSortOptions<SortBy>) => {
  const [sort, setSort] = useState(() => ({
    sortBy: initialBy,
    sortDir: initialDir,
  }));

  const toggleSort = useCallback(
    (value: SortBy) => {
      setSort((current) => getNextTableSort(current, value, defaultDir));
    },
    [defaultDir]
  );

  return { ...sort, toggleSort } as const;
};

export type SortableTableHeaderProps<SortBy extends string> = Omit<
  ThHTMLAttributes<HTMLTableCellElement>,
  "onClick"
> & {
  label: ReactNode;
  value: SortBy;
  active: SortBy;
  dir: SortDir;
  onSort: (value: SortBy) => void;
  align?: "left" | "center" | "right";
  contentClassName?: string;
};

export const SortableTableHeader = <SortBy extends string>({
  label,
  value,
  active,
  dir,
  onSort,
  align = "left",
  className,
  contentClassName,
  ...props
}: SortableTableHeaderProps<SortBy>) => (
  <th
    {...props}
    scope="col"
    className={clsx(
      align === "center" && "text-center",
      align === "right" && "text-right",
      className
    )}
    aria-sort={
      active === value
        ? dir === SortDir.Asc
          ? "ascending"
          : "descending"
        : "none"
    }>
    <button
      type="button"
      className={clsx(
        "table-sort-header",
        align === "center" && "table-sort-header--center",
        align === "right" && "table-sort-header--right",
        contentClassName
      )}
      onClick={() => onSort(value)}>
      {label}
    </button>
  </th>
);
